import { useEffect, useMemo, useState } from 'react';
import { getAdminLeads, updateLeadStatus } from '../services/adminService';

const enquiryOptions = [
  { label: 'All enquiry types', value: '' },
  { label: 'Job related', value: 'job-related' },
  { label: 'Service related', value: 'service-related' },
  { label: 'Investment related', value: 'investment-related' },
  { label: 'Others', value: 'others' },
];

const statusOptions = [
  { label: 'All statuses', value: '' },
  { label: 'New', value: 'new' },
  { label: 'Contacted', value: 'contacted' },
  { label: 'Closed', value: 'closed' },
];

function formatDate(value) {
  if (!value) {
    return 'Not available';
  }

  return new Intl.DateTimeFormat('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function formatLabel(value) {
  return String(value || '')
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function AdminContact() {
  const [filters, setFilters] = useState({ enquiryType: '', status: '', search: '' });
  const [state, setState] = useState({ loading: true, error: '', leads: [] });
  const [selectedLead, setSelectedLead] = useState(null);

  const activeFilters = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(filters)
          .map(([key, value]) => [key, value.trim()])
          .filter(([, value]) => value)
      ),
    [filters]
  );

  useEffect(() => {
    let isMounted = true;
    setState((current) => ({ ...current, loading: true, error: '' }));

    getAdminLeads(activeFilters)
      .then((data) => {
        if (isMounted) {
          setState({ loading: false, error: '', leads: data.leads || [] });
        }
      })
      .catch(() => {
        if (isMounted) {
          setState({ loading: false, error: 'Unable to load leads.', leads: [] });
        }
      });

    return () => {
      isMounted = false;
    };
  }, [activeFilters]);

  const updateFilter = (event) => {
    const { name, value } = event.target;
    setFilters((current) => ({ ...current, [name]: value }));
  };

  const handleStatusUpdate = async (leadId, status) => {
    const result = await updateLeadStatus(leadId, status);
    const updatedLead = result.lead;

    setState((current) => ({
      ...current,
      leads: current.leads.map((lead) => (lead._id === leadId ? updatedLead : lead)),
    }));
    setSelectedLead((current) => (current?._id === leadId ? updatedLead : current));
  };

  return (
    <section className="admin-page">
      <div className="admin-page__header">
        <p className="eyebrow">Contact</p>
        <h1>Submitted leads</h1>
      </div>

      <div className="admin-filters">
        <input
          name="search"
          onChange={updateFilter}
          placeholder="Search name, email, or message"
          type="search"
          value={filters.search}
        />
        <select name="enquiryType" onChange={updateFilter} value={filters.enquiryType}>
          {enquiryOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <select name="status" onChange={updateFilter} value={filters.status}>
          {statusOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {state.error ? <p className="form-alert form-alert--error">{state.error}</p> : null}

      <div className="lead-list">
        {state.leads.map((lead) => (
          <article className="lead-card" key={lead._id}>
            <div className="lead-card__main">
              <div>
                <strong>{lead.name}</strong>
                <span>{lead.email}</span>
                {lead.phone ? <span>{lead.phone}</span> : null}
              </div>
              <p>{lead.message}</p>
            </div>
            <div className="lead-card__meta">
              <span>{formatLabel(lead.enquiryType)}</span>
              <span>Status: {formatLabel(lead.status)}</span>
              <span>Email: {formatLabel(lead.emailStatus)}</span>
              <span>{formatDate(lead.createdAt)}</span>
              <button className="btn btn--secondary" onClick={() => setSelectedLead(lead)} type="button">
                View details
              </button>
            </div>
          </article>
        ))}
        {!state.loading && state.leads.length === 0 ? (
          <p className="admin-empty">No leads match the selected filters.</p>
        ) : null}
        {state.loading ? <p className="admin-empty">Loading leads...</p> : null}
      </div>

      {selectedLead ? (
        <div className="lead-modal" role="dialog" aria-modal="true">
          <div className="lead-modal__panel">
            <div className="lead-modal__header">
              <div>
                <p className="eyebrow">Lead details</p>
                <h2>{selectedLead.name}</h2>
              </div>
              <button className="lead-modal__close" onClick={() => setSelectedLead(null)} type="button">
                Close
              </button>
            </div>
            <div className="lead-detail-grid">
              <span>Email</span>
              <strong>{selectedLead.email}</strong>
              <span>Phone</span>
              <strong>{selectedLead.phone || 'Not provided'}</strong>
              <span>Enquiry type</span>
              <strong>{formatLabel(selectedLead.enquiryType)}</strong>
              <span>Status</span>
              <select
                onChange={(event) => handleStatusUpdate(selectedLead._id, event.target.value)}
                value={selectedLead.status}
              >
                {statusOptions
                  .filter((option) => option.value)
                  .map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
              </select>
              <span>Email status</span>
              <strong>{formatLabel(selectedLead.emailStatus)}</strong>
              <span>Email error</span>
              <strong>{selectedLead.emailError || 'None'}</strong>
              <span>Source</span>
              <strong>{selectedLead.source}</strong>
              <span>Lead ID</span>
              <strong>{selectedLead._id}</strong>
              <span>Created</span>
              <strong>{formatDate(selectedLead.createdAt)}</strong>
              <span>IP address</span>
              <strong>{selectedLead.ipAddress || 'Not available'}</strong>
              <span>User agent</span>
              <strong>{selectedLead.userAgent || 'Not available'}</strong>
            </div>
            <div className="lead-message">
              <h3>Message</h3>
              <p>{selectedLead.message}</p>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}

export default AdminContact;
